import * as THREE from 'three';
import { EDGE_GLOW_OPACITY } from '../../constants';
import { disposeFilamentLine } from '../filamentLines';

const LINE_WHITE = new THREE.Color(0xffffff);
/** Closer than this, a coarse polyline clips on the near plane and stops short of the disk. */
export const EDGE_DENSE_DIST = 48;
const EDGE_DENSE_DIST2 = EDGE_DENSE_DIST * EDGE_DENSE_DIST;
/** Dense rebuilds per zoom frame. Enough to catch the wires in view, not the whole graph. */
export const EDGE_LOD_BUDGET = 32;

function dist2(camera, point) {
  const dx = point.x - camera.position.x;
  const dy = point.y - camera.position.y;
  const dz = point.z - camera.position.z;
  return dx * dx + dy * dy + dz * dz;
}

/** Zoomed-in coarse wires need a dense curve. Idle frames do not ask. */
export function wantsDenseEdge(sampleCount, nearDist2, zooming) {
  return zooming && (sampleCount || 0) < 36 && nearDist2 < EDGE_DENSE_DIST2;
}

export function followNestMotion({
  movedIds,
  edgeLinesByKey,
  meshByTaskId,
  haloByTaskId,
  labelByTaskId,
  placeLabelNearMesh,
  updateEdgeCurveGeometry,
  camera,
  tmpVa,
  tmpVb,
  forceHigh = false,
}) {
  if (!movedIds?.size && !forceHigh) return;
  edgeLinesByKey.forEach((lineObj) => {
    const { fromId, toId, sourceNodeMeta } = lineObj.userData;
    if (!forceHigh && !movedIds.has(fromId) && !movedIds.has(toId)) return;
    const ma = meshByTaskId.get(fromId);
    const mb = meshByTaskId.get(toId);
    if (!ma || !mb) return;
    tmpVa.copy(ma.position);
    tmpVb.copy(mb.position);
    updateEdgeCurveGeometry(lineObj, tmpVa, tmpVb, sourceNodeMeta, camera, forceHigh);
  });
  haloByTaskId.forEach(({ halo, mesh: hm }) => {
    if (movedIds.has(hm.userData.taskId)) {
      halo.position.copy(hm.position);
      halo.position.z -= 0.05;
    }
  });
  movedIds.forEach((tid) => {
    const mesh = meshByTaskId.get(tid);
    const rec = labelByTaskId.get(tid);
    if (mesh && rec) placeLabelNearMesh(mesh, rec.label);
  });
}

export function stepEdges({
  edgeLinesByKey,
  meshByTaskId,
  camera,
  tmpVa,
  tmpVb,
  updateEdgeCurveGeometry,
  finishOccluderClip,
  nodeGroup,
  igniting,
  dragging,
  NEURAL_TRANS_EDGE_FADE_LERP,
  forceHigh = false,
}) {
  const edgeKeysToRemove = [];
  let denseBudget = forceHigh ? EDGE_LOD_BUDGET : 0;

  edgeLinesByKey.forEach((lineObj, ek) => {
    const { fromId, toId, sourceNodeMeta, exiting } = lineObj.userData;
    const ma = meshByTaskId.get(fromId);
    const mb = meshByTaskId.get(toId);
    if (ma && mb) {
      let high = !!lineObj.userData.igniting;
      if (!high && denseBudget > 0 && camera && (lineObj.userData.sampleCount || 0) < 36) {
        const near = Math.min(dist2(camera, ma.position), dist2(camera, mb.position));
        if (wantsDenseEdge(lineObj.userData.sampleCount, near, true)) {
          high = true;
          denseBudget -= 1;
        }
      }
      tmpVa.copy(ma.position);
      tmpVb.copy(mb.position);
      updateEdgeCurveGeometry(
        lineObj,
        tmpVa,
        tmpVb,
        sourceNodeMeta,
        camera,
        high,
      );
    }
    if (!igniting) lineObj.material.color.copy(LINE_WHITE);

    let top = lineObj.userData.targetOpacity ?? 0;
    if (exiting) top = 0;
    const curOp = lineObj.material.opacity ?? 0;
    const nextOp = curOp + (top - curOp) * NEURAL_TRANS_EDGE_FADE_LERP;
    if (Math.abs(nextOp - curOp) > 0.0008 || exiting) {
      lineObj.material.opacity = nextOp;
      lineObj.visible = nextOp > 0.02;
      if (lineObj.userData.glow) {
        lineObj.userData.glow.material.opacity = nextOp * EDGE_GLOW_OPACITY;
        lineObj.userData.glow.visible = lineObj.visible && EDGE_GLOW_OPACITY > 0 && !dragging;
      }
    }

    if (exiting && lineObj.material.opacity < 0.02) {
      edgeKeysToRemove.push(ek);
    }
  });
  finishOccluderClip?.();

  edgeKeysToRemove.forEach((ek) => {
    const lineObj = edgeLinesByKey.get(ek);
    if (!lineObj) return;
    nodeGroup.remove(lineObj);
    disposeFilamentLine(lineObj);
    edgeLinesByKey.delete(ek);
  });
}
